<template>
  <div>
    <h4>
      <label
        for="applicationGuaranteeConsent"
        class="form-label required"
      >
        {{ $t('short_term_application.training_preferences') }}
      </label>
    </h4>
    <div>
      <div class="form-check">
        <input
          id="applicationGuaranteeConsent"
          v-model="applicationData.applicationGuaranteeConsent"
          type="checkbox"
          class="form-check-input"
        >
        <label
          class="form-check-label"
          for="applicationGuaranteeConsent"
        >
          {{ $t('short_term_application.training_preferences_consent') }}
        </label>
      </div>
    </div>

    <hr class="short">

    <div class="card available-exchanges">
      <div class="card-header">
        <span>{{ $t('short_term_application.available_places.title') }}</span>
        <button
          type="button"
          class="btn btn-sm btn-success"
          @click.prevent="showAvailableExchangesModal"
        >
          {{ $t('short_term_application.available_places.show') }}
        </button>
      </div>
    </div>

    <div class="row gx-2">
      <div class="mb-3 col-sm-6">
        <label class="form-label required">{{ $t('short_term_application.nationality') }}</label>
        <select
          v-model="applicationData.nationalityId"
          class="form-select"
          :disabled="!canChangeDetails"
        >
          <option/>
          <option
            v-for="nationality of nationalities"
            :key="nationality.id"
            :value="nationality.id"
          >
            {{ nationality.name }}
          </option>
        </select>
      </div>
      <div
        v-if="lands.length > 0"
        class="mb-3 col-sm-6"
      >
        <label class="form-label required">Land</label>
        <select
          v-model="applicationData.germanyLandId"
          class="form-select"
        >
          <option/>
          <option
            v-for="land of lands"
            :key="land.id"
            :value="land.id"
          >
            {{ land.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="row gx-2">
      <div class="mb-3 col-sm-6">
        <label class="form-label required">{{ $t('short_term_application.ncp') }}</label>
        <select
          v-model="applicationData.ncpId"
          class="form-select"
        >
          <option/>
          <option
            v-for="ncp of ncps"
            :key="ncp.id"
            :value="ncp.id"
          >
            {{ ncp.name }}
          </option>
        </select>
      </div>
      <div
        v-if="requireIdNumber"
        class="mb-3 col-sm-6"
      >
        <label class="form-label required">ID number</label>
        <input
          v-model="applicationData.idNumber"
          type="text"
          class="form-control"
        >
      </div>
    </div>

    <div class="row gx-2">
      <div class="mb-3 col-sm-6">
        <label
          for="function-id"
          class="form-label required"
        >
          {{ $t('short_term_application.function') }}
        </label>
        <select
          id="function-id"
          v-model="applicationData.functionId"
          class="form-select"
          :disabled="!canChangeDetails"
        >
          <option :value="null"/>
          <option
            v-for="aFunction of availableFunctions"
            :key="aFunction.id"
            :value="aFunction.id"
          >
            {{ aFunction.name }}
          </option>
        </select>
      </div>
      <div
        v-if="selectCourtStaffworkplaceVisible"
        class="mb-3 col-sm-6"
      >
        <label
          for="court-staff-workplace-id"
          class="form-label required"
        >
          {{ $t('short_term_application.court_staff_workplace') }}
        </label>
        <select
          id="court-staff-workplace-id"
          v-model="applicationData.courtStaffWorkplaceId"
          class="form-select"
          :disabled="!canChangeDetails"
        >
          <option :value="null"/>
          <option
            v-for="workplace of availableCourtStaffWorkplaces"
            :key="workplace.id"
            :value="workplace.id"
          >
            {{ workplace.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="row gx-2">
      <div class="mb-3 col-sm-6">
        <label
          for="activity-category-id"
          class="form-label required"
        >
          {{ $t('short_term_application.activity_category') }}
        </label>
        <select
          id="activity-category-id"
          v-model="applicationData.activityCategoryId"
          class="form-select"
          :disabled="selectCategoryDisabled"
        >
          <option :value="null"/>
          <option
            v-for="activityCategory of availableActivityCategories"
            :key="activityCategory.id"
            :value="activityCategory.id"
          >
            {{ activityCategory.name }}
          </option>
        </select>
      </div>
    </div>

    <div
      v-if="selectSpecialisationVisible"
      class="row gx-2"
    >
      <div class="mb-3 col-sm-6">
        <label
          for="specialisation-id"
          class="form-label required"
        >
          {{ $t('short_term_application.specialisation') }}
        </label>
        <select
          id="specialisation-id"
          v-model="applicationData.specialisationId"
          class="form-select"
          :disabled="!canChangeDetails"
        >
          <option :value="null"/>
          <option
            v-for="specialisation of availableSpecialisations"
            :key="specialisation.id"
            :value="specialisation.id"
          >
            {{ specialisation.name }}
          </option>
        </select>
      </div>
      <div
        v-if="selectedSpecialisation?.additionalInformation"
        class="mb-3 col-sm-6 d-flex align-items-end"
      >
        <a
          :href="selectedSpecialisation.additionalInformation.url"
          class="btn btn-outline-info fw-bold"
          target="_blank"
          rel="noopener"
        >
          <span
            class="material-icons me-1 align-middle"
            aria-hidden="true"
          >picture_as_pdf</span>
          {{ $t('short_term_application.additional_information') }}
        </a>
      </div>
    </div>

    <!-- Add `|| supportingDocumentVisible` when supporting document uploads are re-enabled. -->
    <div
      v-if="subSpecialisationSelectionVisible"
      class="row gx-2"
    >
      <fieldset
        v-if="subSpecialisationSelectionVisible"
        class="mb-3 col-sm-6"
      >
        <legend class="col-form-label">
          {{ $t('short_term_application.sub_specialisations') }}
        </legend>
        <div
          v-for="subSpecialisation of availableSubSpecialisations"
          :key="subSpecialisation.id"
          class="form-check"
        >
          <input
            :id="`sub-specialisation-${subSpecialisation.id}`"
            v-model="applicationData.subSpecialisationIds"
            :value="subSpecialisation.id"
            :disabled="subSpecialisationSelectionDisabled(subSpecialisation.id)"
            type="checkbox"
            class="form-check-input"
          >
          <label
            class="form-check-label"
            :for="`sub-specialisation-${subSpecialisation.id}`"
          >
            {{ subSpecialisation.name }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="other-sub-specialisation-selected"
            :checked="otherSubSpecialisationSelected"
            :disabled="subSpecialisationSelectionLimitReached && !otherSubSpecialisationSelected"
            type="checkbox"
            class="form-check-input"
            @change="onOtherSubSpecialisationChanged"
          >
          <label
            class="form-check-label"
            for="other-sub-specialisation-selected"
          >
            {{ $t('short_term_application.other_sub_specialisation') }}
          </label>
        </div>
        <div
          v-if="otherSubSpecialisationSelected"
          class="mt-2 mb-0"
        >
          <label
            for="other-sub-specialisation"
            class="form-label required"
          >
            {{ $t('short_term_application.other_sub_specialisation_description') }}
          </label>
          <input
            id="other-sub-specialisation"
            v-model="applicationData.otherSubSpecialisation"
            type="text"
            class="form-control"
            required
          >
        </div>
        <small class="form-text text-muted">
          {{ $t('short_term_application.sub_specialisations_help') }}
        </small>
      </fieldset>

      <!-- Supporting document uploads are temporarily disabled.
      <div
        v-if="supportingDocumentVisible"
        class="mb-3 col-sm-6"
      >
        <label class="form-label">{{ $t('short_term_application.supporting_document') }}</label>
        <upload-field
          v-model="applicationData.supportingDocument"
          :upload-url="supportingDocumentUploadUrl"
        />
        <small class="form-text text-muted">
          {{ $t('short_term_application.supporting_document_help') }}
        </small>
      </div>
      -->
    </div>

    <template v-if="showInstructions">
      <hr class="short">
      <h4>{{ $t('project_based_application.instructions') }}</h4>
      <div
        v-if="instructionsHtml"
        v-html="instructionsHtml"
      />

      <div
        v-for="file of instructionsFiles"
        :key="file.url"
      >
        <a
          :href="file.url"
          target="_blank"
        >
          {{ file.fileName }}
        </a>
      </div>
    </template>

    <hr class="short">

    <div class="choice-cards">
      <short-term-exchange-choice-card
        v-model="applicationData.firstChoice"
        :allow-delete="true"
        :title="$t('short_term_application.first_choice')"
        choice-key="first-choice"
        @show-select-exchange="showSelectExchangeModal(0)"
        @delete-exchange="showDeleteExchangePrompt(0)"
      />
      <short-term-exchange-choice-card
        v-if="secondChoiceCardVisible"
        v-model="applicationData.secondChoice"
        :allow-delete="true"
        :title="$t('short_term_application.second_choice')"
        choice-key="second-choice"
        @show-select-exchange="showSelectExchangeModal(1)"
        @delete-exchange="showDeleteExchangePrompt(1)"
      />
      <short-term-exchange-choice-card
        v-if="thirdChoiceCardVisible"
        v-model="applicationData.thirdChoice"
        :allow-delete="true"
        :title="$t('short_term_application.third_choice')"
        choice-key="third-choice"
        @show-select-exchange="showSelectExchangeModal(2)"
        @delete-exchange="showDeleteExchangePrompt(2)"
      />
    </div>
    <div
      v-if="addSecondChoiceButtonVisible || addThirdChoiceButtonVisible"
      class="mt-4"
    >
      <button
        v-if="addSecondChoiceButtonVisible"
        type="button"
        class="btn btn-success btn-sm"
        @click.prevent="showSelectExchangeModal(1)"
      >
        {{ $t('short_term_application.add_second_choice') }}
      </button>
      <button
        v-if="addThirdChoiceButtonVisible"
        type="button"
        class="btn btn-success btn-sm"
        @click.prevent="showSelectExchangeModal(2)"
      >
        {{ $t('short_term_application.add_third_choice') }}
      </button>
    </div>
    <div
      v-if="otherCountryAllocationVisible"
      class="row gx-2 mt-4"
    >
      <div class="mb-3 col-12">
        <label class="form-label required">{{ $t('application_form.other_country_allocation') }}</label>
        <div>
          <div class="form-check form-check-inline">
            <input
              :id="`other-country-allocation-yes`"
              v-model="applicationData.allocateToOtherCountry"
              :value="true"
              type="radio"
              class="form-check-input"
            >
            <label
              class="form-check-label"
              :for="`other-country-allocation-yes`"
            >
              {{ $t('option.yes') }}
            </label>
          </div>
          <div class="form-check form-check-inline">
            <input
              :id="`other-country-allocation-no`"
              v-model="applicationData.allocateToOtherCountry"
              :value="false"
              type="radio"
              class="form-check-input"
            >
            <label
              class="form-check-label"
              :for="`other-country-allocation-no`"
            >
              {{ $t('option.no') }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <teleport
      to="#vue-portal-target"
      append
    >
      <ts-modal
        id="availableExchangesModal"
        ref="availableExchangesModal"
        :modal-title="$t('short_term_application.available_exchanges.title')"
        modal-class="available-exchanges-modal"
        modal-dialog-class="modal-xl"
      >
        <template #modal-body>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
              <tr>
                <th>{{ $t('short_term_application.available_exchanges.column.country') }}</th>
                <th>{{ $t('short_term_application.available_exchanges.column.category') }}</th>
                <th>{{ $t('short_term_application.available_exchanges.column.type') }}</th>
                <th>{{ $t('short_term_application.available_exchanges.column.languages') }}</th>
                <th>{{ $t('short_term_application.available_exchanges.column.functions') }}</th>
                <th>{{ $t('short_term_application.available_exchanges.column.specialisation') }}</th>
                <th>{{ $t('short_term_application.available_exchanges.column.available_places') }}</th>
                <th>{{ $t('short_term_application.available_exchanges.column.length') }}</th>
                <th>{{ $t('short_term_application.available_exchanges.column.additional_information') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr class="filter no-hover">
                <td>
                  <grid-filter-list
                    :show-dropdown-caret="false"
                    :filter="countryFilter"
                    :model-value="countryFilter.selectedValueArray"
                    @update:model-value="onFilterChanged(countryFilter, $event)"
                  >
                    {{ filterTitle(countryFilter) }}
                  </grid-filter-list>
                </td>
                <td>
                  <grid-filter-list
                    :show-dropdown-caret="false"
                    :filter="categoryFilter"
                    :model-value="categoryFilter.selectedValueArray"
                    @update:model-value="onFilterChanged(categoryFilter, $event)"
                  >
                    {{ filterTitle(categoryFilter) }}
                  </grid-filter-list>
                </td>
                <td>
                  <grid-filter-list
                    :show-dropdown-caret="false"
                    :filter="typeFilter"
                    :model-value="typeFilter.selectedValueArray"
                    @update:model-value="onFilterChanged(typeFilter, $event)"
                  >
                    {{ filterTitle(typeFilter) }}
                  </grid-filter-list>
                </td>
                <td>
                  <grid-filter-list
                    :show-dropdown-caret="false"
                    :filter="languageFilter"
                    :model-value="languageFilter.selectedValueArray"
                    @update:model-value="onFilterChanged(languageFilter, $event)"
                  >
                    {{ filterTitle(languageFilter) }}
                  </grid-filter-list>
                </td>
                <td>
                  <grid-filter-list
                    :show-dropdown-caret="false"
                    :filter="functionFilter"
                    :model-value="functionFilter.selectedValueArray"
                    @update:model-value="onFilterChanged(functionFilter, $event)"
                  >
                    {{ filterTitle(functionFilter) }}
                  </grid-filter-list>
                </td>
                <td>
                  <grid-filter-list
                    :show-dropdown-caret="false"
                    :filter="specialisationFilter"
                    :model-value="specialisationFilter.selectedValueArray"
                    @update:model-value="onFilterChanged(specialisationFilter, $event)"
                  >
                    {{ filterTitle(specialisationFilter) }}
                  </grid-filter-list>
                </td>
                <td></td>
                <td>
                  <grid-filter-list
                    :show-dropdown-caret="false"
                    :filter="durationFilter"
                    :model-value="durationFilter.selectedValueArray"
                    @update:model-value="onFilterChanged(durationFilter, $event)"
                  >
                    {{ filterTitle(durationFilter) }}
                  </grid-filter-list>
                </td>
                <td></td>
              </tr>
              <tr
                v-for="exchange of filteredExchanges"
                :key="exchange.id"
              >
                <td>{{ exchange.countryName }}</td>
                <td>{{ exchange.category }}</td>
                <td>{{ exchange.exchangeType }}</td>
                <td>{{ exchange.languages }}</td>
                <td>{{ exchange.functions }}</td>
                <td>{{ exchange.specialisation }}</td>
                <td>{{ exchange.availablePlaces }}</td>
                <td>{{ exchange.duration }}</td>
                <td>{{ exchange.additionalInformation }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </template>
        <template #modal-footer>
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            {{ $t('modal.button.close') }}
          </button>
        </template>
      </ts-modal>

      <remote-modal
        ref="selectExchangeModal"
        :modal-title="$t('application_form.select_exchange.title')"
        modal-class="modal-tabular"
        modal-dialog-class="modal-xl"
        :loading-message="$t('application_form.select_exchange.loading')"
        :loading-indicator-in-footer="false"
        :hide-content-when-loading="true"
        @details-loaded="onSelectExchangesLoaded"
      >
        <template
          v-if="selectExchangesListLoaded"
          v-slot:modal-content
        >
          <div
            v-if="selectExchangesList.length > 0"
            class="table-wrapper"
          >
            <div class="table-responsive">
              <table class="table table-hover table-sm">
                <thead>
                <tr>
                  <th>{{ $t('application_form.choice.country') }}</th>
                  <th>{{ $t('application_form.choice.type') }}</th>
                  <th>{{ $t('application_form.choice.language') }}</th>
                  <th>{{ $t('application_form.choice.public') }}</th>
                  <th v-if="requireSpecialisation">{{ $t('application_form.choice.specialisation') }}</th>
                  <th>{{ $t('application_form.choice.available_places') }}</th>
                  <th>{{ $t('application_form.choice.duration') }}</th>
                  <th>{{ $t('application_form.choice.additional_information') }}</th>
                  <th/>
                </tr>
                </thead>
                <tbody>
                <tr class="filter no-hover">
                  <td>
                    <grid-filter-list
                      :show-dropdown-caret="false"
                      :filter="selectCountryFilter"
                      :model-value="selectCountryFilter.selectedValueArray"
                      @update:model-value="onFilterChanged(selectCountryFilter, $event)"
                    >
                      {{ filterTitle(selectCountryFilter) }}
                    </grid-filter-list>
                  </td>
                  <td>
                    <grid-filter-list
                      :show-dropdown-caret="false"
                      :filter="selectTypeFilter"
                      :model-value="selectTypeFilter.selectedValueArray"
                      @update:model-value="onFilterChanged(selectTypeFilter, $event)"
                    >
                      {{ filterTitle(selectTypeFilter) }}
                    </grid-filter-list>
                  </td>
                  <td>
                    <grid-filter-list
                      :show-dropdown-caret="false"
                      :filter="selectLanguageFilter"
                      :model-value="selectLanguageFilter.selectedValueArray"
                      @update:model-value="onFilterChanged(selectLanguageFilter, $event)"
                    >
                      {{ filterTitle(selectLanguageFilter) }}
                    </grid-filter-list>
                  </td>
                  <td>
                    <grid-filter-list
                      :show-dropdown-caret="false"
                      :filter="selectFunctionFilter"
                      :model-value="selectFunctionFilter.selectedValueArray"
                      @update:model-value="onFilterChanged(selectFunctionFilter, $event)"
                    >
                      {{ filterTitle(selectFunctionFilter) }}
                    </grid-filter-list>
                  </td>
                  <td v-if="requireSpecialisation">
                    <grid-filter-list
                      :show-dropdown-caret="false"
                      :filter="selectSpecialisationFilter"
                      :model-value="selectSpecialisationFilter.selectedValueArray"
                      @update:model-value="onFilterChanged(selectSpecialisationFilter, $event)"
                    >
                      {{ filterTitle(selectSpecialisationFilter) }}
                    </grid-filter-list>
                  </td>
                  <td></td>
                  <td>
                    <grid-filter-list
                      :show-dropdown-caret="false"
                      :filter="selectDurationFilter"
                      :model-value="selectDurationFilter.selectedValueArray"
                      @update:model-value="onFilterChanged(selectDurationFilter, $event)"
                    >
                      {{ filterTitle(selectDurationFilter) }}
                    </grid-filter-list>
                  </td>
                  <td></td>
                </tr>
                <tr
                  v-for="exchange of filteredSelectExchangesList"
                  :key="exchange.id"
                >
                  <td>{{ exchange.countryName }}</td>
                  <td>{{ exchange.exchangeType }}</td>
                  <td>{{ exchange.languages }}</td>
                  <td>{{ exchange.functions }}</td>
                  <td v-if="requireSpecialisation">{{ exchange.specialisation }}</td>
                  <td>{{ exchange.availablePlaces }}</td>
                  <td>{{ exchange.duration }}</td>
                  <td>{{ exchange.additionalInformation }}</td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-sm btn-info"
                      @click.prevent="selectExchange(exchange, exchangeSelectionIdx)"
                    >
                      {{ $t('application_form.select_exchange.button.select') }}
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div
            v-else
            class="alert alert-warning"
          >
            {{ $t('application_form.select_exchange.no_exchanges_found') }}
          </div>
        </template>
        <template v-slot:modal-footer>
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
          >
            {{ $t('modal.button.close') }}
          </button>
        </template>
      </remote-modal>
      <modal-alert
        ref="modalAlert"
        :close-button-title="$t('modal.button.close')"
      />
      <modal-confirmation-rails
        ref="removeChoiceConfirmationModal"
        @confirmed="onRemoveChoiceConfirmed"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted, useTemplateRef} from 'vue'
import {useTranslation} from "i18next-vue"
import Jsona from 'jsona'

import {isNonEmptyString} from "@/js/js-utils";
import {createFilter, filterItems} from "@/js/vue-components/filter-utils";
import RemoteModal from '../../vue-components/remote-modal.vue'
import TsModal from '../../vue-components/ts-modal.vue'
import ModalAlert from '../../vue-components/modal-alert.vue'
import ModalConfirmationRails from '../../vue-components/modal-confirmation-rails.vue'
import ShortTermExchangeChoiceCard from './short-term-exchange-choice-card.vue'
import GridFilterList from "@/js/vue-components/grid-filter-list.vue"
// Supporting document uploads are temporarily disabled.
// import UploadField from '../../vue-components/upload-field.vue'

import type {
  ExchangePlace,
  ExchangeChoice,
  IdNamePair,
  OspeActivityType,
  OspeFunction,
  OspeNationality,
  OspeNcp,
  OspeSpecialisation,
  ShortTermApplication,
  OspeShortTermAvailableFunction,
  OspeShortTermAvailableCourtStaffWorkplace,
  OspeShortTermAvailableCategory,
  OspeShortTermAvailableSpecialisation, OspeProjectBasedActivityTemplateFile, OspeShortTermActivityCategoryInstructions
} from '../types'
import {GridFilter} from "@/js/vue-components/types";

const props = defineProps({
  availableExchangesUrl: {default: null, type: String},
  supportingDocumentUploadUrl: {default: null, type: String},
})

const {t: $t} = useTranslation()
const MAX_SUB_SPECIALISATIONS = 3

// Template refs
const availableExchangesModal = useTemplateRef<InstanceType<typeof TsModal>>('availableExchangesModal')
const selectExchangeModal = useTemplateRef<InstanceType<typeof RemoteModal>>('selectExchangeModal')
const modalAlert = useTemplateRef<InstanceType<typeof ModalAlert>>('modalAlert')
const removeChoiceConfirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmationRails>>('removeChoiceConfirmationModal')

// Reactive state
const applicationData = ref<ShortTermApplication>({
  applicationGuaranteeConsent: false,
  activityCategoryId: null,
  functionId: null,
  courtStaffWorkplaceId: null,
  specialisationId: null,
  firstChoice: null,
  secondChoice: null,
  thirdChoice: null,
  nationalityId: null,
  germanyLandId: null,
  ncpId: null,
  idNumber: null,
  allocateToOtherCountry: null,
  subSpecialisationIds: [],
  otherSubSpecialisation: null,
  supportingDocument: {id: '', filename: '', url: ''},
})

const activityTypes = ref<OspeActivityType[]>([])
const selectedActivityType = ref<OspeActivityType | null>(null)
const availableExchanges = ref<ExchangePlace[]>([])
const selectExchangesList = ref<ExchangeChoice[]>([])
const selectExchangesListLoaded = ref(false)
const exchangeSelectionIdx = ref(0)
const applicationDataEl = ref<HTMLInputElement | null>(null)
const nationalities = ref<OspeNationality[]>([])
const availableFunctions = ref<OspeShortTermAvailableFunction[]>([])
const instructions = ref<{ [id: string]: OspeShortTermActivityCategoryInstructions }>({})
const applicationDataInitialized = ref(false)

const countryFilter = ref(new GridFilter({type: "list"}))
const categoryFilter = ref(new GridFilter({type: "list"}))
const typeFilter = ref(new GridFilter({type: "list"}))
const languageFilter = ref(new GridFilter({type: "list"}))
const functionFilter = ref(new GridFilter({type: "list"}))
const specialisationFilter = ref(new GridFilter({type: "list"}))
const durationFilter = ref(new GridFilter({type: "list"}))

const selectCountryFilter = ref(new GridFilter({type: "list"}))
const selectTypeFilter = ref(new GridFilter({type: "list"}))
const selectLanguageFilter = ref(new GridFilter({type: "list"}))
const selectFunctionFilter = ref(new GridFilter({type: "list"}))
const selectSpecialisationFilter = ref(new GridFilter({type: "list"}))
const selectDurationFilter = ref(new GridFilter({type: "list"}))

// Computed properties
const selectedActivityCategory = computed((): OspeShortTermAvailableCategory | null => {
  let categories = availableActivityCategories.value
  if (requireCourtStaffWorkplace.value) {
    const currentWorkplace = selectedCourtStaffWorkplace.value
    if (!currentWorkplace) {
      return null
    }
    categories = categories.filter(v => v.courtStaffWorkplaceIds.includes(currentWorkplace.id))
  }
  return categories.find(v => v.id == applicationData.value.activityCategoryId) || null
})

const selectCategoryDisabled = computed((): boolean => {
  if (!canChangeDetails.value) {
    return true
  }

  if (requireCourtStaffWorkplace.value) {
    return applicationData.value.courtStaffWorkplaceId == null
  }

  return selectedFunction.value == null
})

const requireSpecialisation = computed((): boolean => {
  return availableSpecialisations.value.length > 0
})

const hasSpecialisation = computed((): boolean => {
  return applicationData.value.specialisationId !== null
})

const hasFunction = computed((): boolean => {
  return applicationData.value.functionId !== null
})

const hasNationality = computed((): boolean => {
  return applicationData.value.nationalityId !== null
})

const selectSpecialisationVisible = computed((): boolean => {
  return availableSpecialisations.value.length > 0
})

const selectedSpecialisation = computed((): OspeShortTermAvailableSpecialisation | null => {
  return availableSpecialisations.value.find(
    specialisation => specialisation.id == applicationData.value.specialisationId,
  ) || null
})

const otherCountryAllocationVisible = computed((): boolean => {
  return selectedSpecialisation.value?.withSubSpecialisations !== true
})

const availableSubSpecialisations = computed((): IdNamePair[] => {
  return selectedSpecialisation.value?.subSpecialisations || []
})

const subSpecialisationSelectionVisible = computed((): boolean => {
  return selectedSpecialisation.value?.withSubSpecialisations === true &&
    availableSubSpecialisations.value.length > 0
})

const supportingDocumentVisible = computed((): boolean => {
  return selectedSpecialisation.value?.allowSupportingDocuments === true
})

const otherSubSpecialisationSelected = computed((): boolean => {
  return applicationData.value.otherSubSpecialisation !== null
})

const subSpecialisationSelectionCount = computed((): number => {
  return applicationData.value.subSpecialisationIds.length +
    (otherSubSpecialisationSelected.value ? 1 : 0)
})

const subSpecialisationSelectionLimitReached = computed((): boolean => {
  return subSpecialisationSelectionCount.value >= MAX_SUB_SPECIALISATIONS
})

// TODO: remove method
const functions = computed((): OspeFunction[] => {
  if (selectedActivityType.value) {
    if (selectedActivityType.value.requiresSpecialisation) {
      // get functions from the selected specialisations
      if (Array.isArray(selectedActivityType.value.specialisations)) {
        const specialisation = selectedActivityType.value.specialisations.find(spec => spec.id == applicationData.value.specialisationId)
        if (specialisation) {
          return specialisation.functions
        }
      }
    } else if (Array.isArray(selectedActivityType.value.functions)) {
      return selectedActivityType.value.functions
    }
  }
  return []
})

// TODO: remove
const specialisations = computed((): OspeSpecialisation[] => {
  if (selectedActivityType.value) {
    if (selectedActivityType.value.requiresSpecialisation) {
      if (Array.isArray(selectedActivityType.value.specialisations)) {
        return selectedActivityType.value.specialisations
      }
    }
  }
  return []
})

const secondChoiceCardVisible = computed((): boolean => {
  return applicationData.value.secondChoice != null || applicationData.value.thirdChoice != null
})

const addSecondChoiceButtonVisible = computed((): boolean => {
  if (applicationData.value.firstChoice) {
    return !secondChoiceCardVisible.value
  }
  return false
})

const thirdChoiceCardVisible = computed((): boolean => {
  return applicationData.value.thirdChoice != null
})

const addThirdChoiceButtonVisible = computed((): boolean => {
  if (applicationData.value.firstChoice) {
    return secondChoiceCardVisible.value && !thirdChoiceCardVisible.value
  }
  return false
})

const lands = computed((): IdNamePair[] => {
  const nationality = nationalities.value.find(n => n.id === applicationData.value.nationalityId)
  if (nationality) {
    return nationality.lands || []
  }
  return []
})

const requireIdNumber = computed((): boolean => {
  return selectedNcp.value?.requireIdNumber || false
})

const canChangeDetails = computed((): boolean => {
  return applicationData.value.firstChoice == null
})

const selectedNationality = computed((): OspeNationality | null => {
  return nationalities.value.find(n => n.id === applicationData.value.nationalityId) || null
})

const ncps = computed((): OspeNcp[] => {
  return selectedNationality.value?.ncps || []
})

const selectedNcp = computed((): OspeNcp | null => {
  return selectedNationality.value?.ncps?.find(n => n.id == applicationData.value.ncpId) || null
})

const selectedFunction = computed((): OspeShortTermAvailableFunction | null => {
  return availableFunctions.value.find(fn => fn.id == applicationData.value.functionId) || null
})

const selectedCourtStaffWorkplace = computed((): OspeShortTermAvailableCourtStaffWorkplace | null => {
  return availableCourtStaffWorkplaces.value.find(wp => wp.id == applicationData.value.courtStaffWorkplaceId) || null
})

const selectCourtStaffworkplaceVisible = computed((): boolean => {
  return availableCourtStaffWorkplaces.value.length > 0 || false
})

const availableCourtStaffWorkplaces = computed((): OspeShortTermAvailableCourtStaffWorkplace[] => {
  return selectedFunction.value?.courtStaffWorkplaces || []
})

const availableActivityCategories = computed((): OspeShortTermAvailableCategory[] => {
  let allCategories = selectedFunction.value?.categories || []

  const currentCourtStaffWorkplace = selectedCourtStaffWorkplace.value
  if (currentCourtStaffWorkplace) {
    allCategories = allCategories.filter(c => c.courtStaffWorkplaceIds.includes(currentCourtStaffWorkplace.id))
  } else {
    if (requireCourtStaffWorkplace.value) { // function has court staff workplaces, but none selected
      return []
    }
  }
  return allCategories
})

const availableSpecialisations = computed((): OspeShortTermAvailableSpecialisation[] => {
  const currentCategory = selectedActivityCategory.value
  if (!currentCategory) {
    return []
  }

  if (requireCourtStaffWorkplace.value) {
    const currentCourtStaffWorkplace = selectedCourtStaffWorkplace.value
    if (currentCourtStaffWorkplace == null) {
      return []
    }

    return currentCategory.specialisations.filter(c => c.courtStaffWorkplaceIds.includes(currentCourtStaffWorkplace.id))
  }

  return currentCategory.specialisations
})

const requireCourtStaffWorkplace = computed((): boolean => {
  return (selectedFunction.value?.courtStaffWorkplaces?.length || 0) > 0
})

const showInstructions = computed((): boolean => {
  return currentInstructions.value != null
})

const currentInstructions = computed((): OspeShortTermActivityCategoryInstructions | null => {
  const currentCategory = selectedActivityCategory.value
  if (!currentCategory) {
    return null
  }
  return instructions.value[currentCategory.id.toString()] || null
})

const instructionsHtml = computed((): string | null => {
  return currentInstructions.value?.html || null
})

const instructionsFiles = computed((): OspeProjectBasedActivityTemplateFile[] => {
  return currentInstructions.value?.files || []
})

const filteredExchanges = computed((): ExchangePlace[] => {
  const selectedCountries = filterItems(countryFilter.value)
  const selectedCategories = filterItems(categoryFilter.value)
  const selectedTypes = filterItems(typeFilter.value)
  const selectedLanguages = filterItems(languageFilter.value)
  const selectedFunctions = filterItems(functionFilter.value)
  const selectedSpecialisations = filterItems(specialisationFilter.value)
  const selectedDurations = filterItems(durationFilter.value)

  return availableExchanges.value.filter((f) => {
    if (selectedCountries.length > 0 && !selectedCountries.includes(f.countryName)) {
      return false
    }

    if (selectedCategories.length > 0 && !selectedCategories.includes(f.category)) {
      return false
    }

    if (selectedTypes.length > 0) {
      const exchangeTypes = (f.exchangeType || '').split(', ')
      if (!selectedTypes.find(l => exchangeTypes.includes(l))) {
        return false
      }
    }

    if (selectedLanguages.length > 0) {
      const exchangeLanguages = f.languages.split(', ')
      if (!selectedLanguages.find(l => exchangeLanguages.includes(l))) {
        return false
      }
    }

    if (selectedFunctions.length > 0) {
      const exchangeFunctions = f.functions.split(', ')
      if (!selectedFunctions.find(l => exchangeFunctions.includes(l))) {
        return false
      }
    }

    if (selectedSpecialisations.length > 0 && !selectedSpecialisations.includes(f.specialisation || '')) {
      return false
    }

    if (selectedDurations.length > 0) {
      const exchangeDurations = f.duration.split(', ')
      if (!selectedDurations.find(l => exchangeDurations.includes(l))) {
        return false
      }
    }

    return true
  })
})

const filteredSelectExchangesList = computed((): ExchangeChoice[] => {
  const selectedCountries = filterItems(selectCountryFilter.value)
  const selectedTypes = filterItems(selectTypeFilter.value)
  const selectedLanguages = filterItems(selectLanguageFilter.value)
  const selectedFunctions = filterItems(selectFunctionFilter.value)
  const selectedSpecialisations = filterItems(selectSpecialisationFilter.value)
  const selectedDurations = filterItems(selectDurationFilter.value)

  const selectedPlaceIds = [
    applicationData.value.firstChoice?.id,
    applicationData.value.secondChoice?.id,
    applicationData.value.thirdChoice?.id
  ].filter(v => v != null).map(v => v?.toString() || '')

  return selectExchangesList.value.filter((f) => {
    if (f.activityId && selectedPlaceIds.includes(f.id.toString())) {
      return false // don't show activities already selected for a choice
    }

    if (selectedCountries.length > 0 && !selectedCountries.includes(f.countryName)) {
      return false
    }

    if (selectedTypes.length > 0) {
      const exchangeTypes = (f.exchangeType || '').split(', ')
      if (!selectedTypes.find(l => exchangeTypes.includes(l))) {
        return false
      }
    }

    if (selectedLanguages.length > 0) {
      const exchangeLanguages = f.languages.split(', ')
      if (!selectedLanguages.find(l => exchangeLanguages.includes(l))) {
        return false
      }
    }

    if (selectedFunctions.length > 0) {
      const exchangeFunctions = f.functions.split(', ')
      if (!selectedFunctions.find(l => exchangeFunctions.includes(l))) {
        return false
      }
    }

    if (selectedSpecialisations.length > 0 && !selectedSpecialisations.includes(f.specialisation || '')) {
      return false
    }

    if (selectedDurations.length > 0) {
      const exchangeDurations = f.duration.split(', ')
      if (!selectedDurations.find(l => exchangeDurations.includes(l))) {
        return false
      }
    }

    return true
  })
})

// Watchers
watch(applicationData, () => {
  storeApplicationData()
}, {deep: true})

watch(() => applicationData.value.functionId, () => {
  const currentFunction = selectedFunction.value
  if (currentFunction) {
    const hasCourtStaffWorkplace = currentFunction.courtStaffWorkplaces.some(v => v.id == applicationData.value.courtStaffWorkplaceId)
    if (!hasCourtStaffWorkplace) { // reset court staff workplace
      applicationData.value.courtStaffWorkplaceId = null
    }
  }

  const category = availableActivityCategories.value.find(v => v.id == applicationData.value.activityCategoryId)
  applicationData.value.activityCategoryId = category?.id || null
})

watch(otherCountryAllocationVisible, (visible) => {
  if (!visible) {
    applicationData.value.allocateToOtherCountry = null
  }
})

watch(() => applicationData.value.specialisationId, (currentId, previousId) => {
  if (!applicationDataInitialized.value || currentId == previousId) {
    return
  }

  applicationData.value.subSpecialisationIds = []
  applicationData.value.otherSubSpecialisation = null
  if (!supportingDocumentVisible.value) {
    applicationData.value.supportingDocument = {id: '', filename: '', url: ''}
  }
}, {flush: 'sync'})

// Methods
const showAvailableExchangesModal = (): void => {
  availableExchangesModal.value?.show()
}

const onOtherSubSpecialisationChanged = (event: Event): void => {
  const checkbox = event.target
  if (!(checkbox instanceof HTMLInputElement)) {
    return
  }

  applicationData.value.otherSubSpecialisation = checkbox.checked ? '' : null
}

const subSpecialisationSelectionDisabled = (id: number): boolean => {
  return subSpecialisationSelectionLimitReached.value &&
    !applicationData.value.subSpecialisationIds.includes(id)
}

const normalizeStoredSubSpecialisationSelections = (): void => {
  if (!subSpecialisationSelectionVisible.value) {
    applicationData.value.subSpecialisationIds = []
    applicationData.value.otherSubSpecialisation = null
    return
  }

  const availableIds = new Set(availableSubSpecialisations.value.map(item => item.id))
  applicationData.value.subSpecialisationIds = Array.from(
    new Set(applicationData.value.subSpecialisationIds.filter(id => availableIds.has(id))),
  )
}

const showSelectExchangeModal = (choiceIdx: number): void => {
  if (!selectedActivityCategory.value) {
    modalAlert.value?.show($t('application_form.select_exchange.error.no_exchange_selected.title'), $t('application_form.select_exchange.error.no_exchange_selected.message'), 'danger')
    return
  }

  if (requireSpecialisation.value && !hasSpecialisation.value) {
    modalAlert.value?.show($t('application_form.select_exchange.error.no_specialisation_selected.title'), $t('application_form.select_exchange.error.no_specialisation_selected.message'), 'danger')
    return
  }

  if (!hasFunction.value) {
    modalAlert.value?.show($t('application_form.select_exchange.error.no_function_selected.title'), $t('application_form.select_exchange.error.no_function_selected.message'), 'danger')
    return
  }

  if (requireCourtStaffWorkplace.value && !applicationData.value.courtStaffWorkplaceId) {
    modalAlert.value?.show($t('application_form.select_exchange.error.no_court_staff_workplace_selected.title'), $t('application_form.select_exchange.error.no_court_staff_workplace_selected.message'), 'danger')
    return
  }

  if (!hasNationality.value) {
    modalAlert.value?.show($t('application_form.select_exchange.error.no_nationality_selected.title'), $t('application_form.select_exchange.error.no_nationality_selected.message'), 'danger')
    return
  }

  selectExchangesListLoaded.value = false
  exchangeSelectionIdx.value = choiceIdx

  selectExchangeModal.value?.showModal(props.availableExchangesUrl, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    activity_category_id: selectedActivityCategory.value.id,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    specialisation_id: applicationData.value.specialisationId,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    function_id: applicationData.value.functionId,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    court_staff_workplace_id: applicationData.value.courtStaffWorkplaceId,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    except_nationality_id: applicationData.value.nationalityId,
  })
}

const onSelectExchangesLoaded = (data: any): void => {
  selectExchangesListLoaded.value = true
  const loadedData = new Jsona().deserialize(data)
  if (Array.isArray(loadedData)) {
    selectExchangesList.value = loadedData as ExchangeChoice[]
    updateSelectExchangesListFilters()
  }
}

const selectExchange = (exchange: ExchangeChoice | null, exchangeIdx: number): void => {
  selectExchangeModal.value?.hideModal()
  let previousExchange = null
  let newExchange = null

  if (exchange) {
    switch (exchangeIdx) {
      case 0:
        previousExchange = applicationData.value.firstChoice
        break;
      case 1:
        previousExchange = applicationData.value.secondChoice
        break;
      case 2:
        previousExchange = applicationData.value.thirdChoice
        break;
    }
    newExchange = prepareExchange(exchange, previousExchange)
  }

  switch (exchangeIdx) {
    case 0:
      applicationData.value.firstChoice = newExchange
      break;
    case 1:
      applicationData.value.secondChoice = newExchange
      break;
    case 2:
      applicationData.value.thirdChoice = newExchange
      break;
  }
}

const prepareExchange = (exchange: ExchangeChoice, previousExchange: ExchangeChoice | null): ExchangeChoice => {
  // copy exchange
  exchange = JSON.parse(JSON.stringify(exchange))

  if (previousExchange) {
    if (previousExchange.exchangeTypeId) {
      const typeExists = exchange.exchangeTypeItems.some(x => x.id == previousExchange.exchangeTypeId)
      if (typeExists) {
        exchange.exchangeTypeId = previousExchange.exchangeTypeId
      }
    }
    if (previousExchange.languageId) {
      const languageExists = exchange.languageItems.some(x => x.id == previousExchange.languageId)
      if (languageExists) {
        exchange.languageId = previousExchange.languageId
      }
    }
    if (previousExchange.durationId) {
      const durationExists = exchange.durationItems.some(x => x.id == previousExchange.durationId)
      if (durationExists) {
        exchange.durationId = previousExchange.durationId
      }
    }
    exchange.preferredPeriod = previousExchange.preferredPeriod
    if (exchange.allowPreMatchedParticipantToBeNamed) {
      exchange.preMatchCounterpartName = previousExchange.preMatchCounterpartName
    }
    if (exchange.askAboutHostingMultipleCounterparts) {
      exchange.hasMultipleCounterparts = previousExchange.hasMultipleCounterparts
    }
  }
  return exchange
}

const storeApplicationData = (): void => {
  if (applicationDataEl.value) {
    applicationDataEl.value.value = JSON.stringify(applicationData.value)
  }
}

const showDeleteExchangePrompt = (exchangeIdx: number): void => {
  removeChoiceConfirmationModal.value?.show(null, {
    title: $t('application_form.select_exchange.remove_choice_confirmation.title'),
    message: $t('application_form.select_exchange.remove_choice_confirmation.message'),
    type: 'danger',
    cancelActionTitle: $t('application_form.select_exchange.remove_choice_confirmation.cancel_action_title'),
    confirmActionTitle: $t('application_form.select_exchange.remove_choice_confirmation.confirm_action_title'),
    confirmationCode: null,
    context: exchangeIdx
  })
}

const onRemoveChoiceConfirmed = (context: unknown): void => {
  removeChoiceConfirmationModal.value?.hide()
  const exchangeIdx = typeof context === 'number' ? context : undefined
  switch (exchangeIdx) {
    case 0:
      applicationData.value.firstChoice = applicationData.value.secondChoice
    case 1:
      applicationData.value.secondChoice = applicationData.value.thirdChoice
    case 2:
      applicationData.value.thirdChoice = null
  }
}

const updateAvailableExchangesFilters = (): void => {
  const countries: string[] = []
  const categories: string[] = []
  let types: string[] = []
  let languages: string[] = []
  let functions: string[] = []
  const specialisations: string[] = []
  let durations: string[] = []

  for (const exchange of availableExchanges.value) {
    countries.push(exchange.countryName)
    categories.push(exchange.category)
    types = types.concat((exchange.exchangeType || '').split((', ')))
    languages = languages.concat(exchange.languages.split(', '))
    functions = functions.concat(exchange.functions.split(', '))
    specialisations.push(exchange.specialisation || '')
    durations = durations.concat(exchange.duration.split(', '))
  }

  countryFilter.value = createFilter(countries)
  categoryFilter.value = createFilter(categories)
  typeFilter.value = createFilter(types)
  languageFilter.value = createFilter(languages)
  functionFilter.value = createFilter(functions)
  specialisationFilter.value = createFilter(specialisations)
  durationFilter.value = createFilter(durations)
}

const updateSelectExchangesListFilters = (): void => {
  const countries: string[] = []
  let types: string[] = []
  let languages: string[] = []
  let functions: string[] = []
  const specialisations: string[] = []
  let durations: string[] = []

  for (const exchange of selectExchangesList.value) {
    countries.push(exchange.countryName)
    types = types.concat((exchange.exchangeType || '').split(', '))
    languages = languages.concat(exchange.languages.split(', '))
    functions = functions.concat(exchange.functions.split(', '))
    specialisations.push(exchange.specialisation || '')
    durations = durations.concat(exchange.duration.split(', '))
  }

  selectCountryFilter.value = createFilter(countries)
  selectTypeFilter.value = createFilter(types)
  selectLanguageFilter.value = createFilter(languages)
  selectFunctionFilter.value = createFilter(functions)
  selectSpecialisationFilter.value = createFilter(specialisations)
  selectDurationFilter.value = createFilter(durations)
}

const onFilterChanged = (filter: GridFilter, filterValue: string | string[]): void => {
  filter.selectedValue = filterValue
}

const filterTitle = (filter: GridFilter): string => {
  const title = filter.selectedTitle
  if (isNonEmptyString(title)) {
    return title
  }

  return 'All'
}

// Lifecycle
onMounted(() => {
  const exchangesDataEl = document.querySelector('script[ref="exchangeSelectionData"]')
  if (exchangesDataEl instanceof HTMLScriptElement && exchangesDataEl.type == 'application/json') {
    try {
      const exchangeData = JSON.parse(exchangesDataEl.innerText)
      if (Array.isArray(exchangeData.functions)) {
        availableFunctions.value = exchangeData.functions
      }
      if (Array.isArray(exchangeData.activityTypes)) {
        activityTypes.value = exchangeData.activityTypes
      }
      if (Array.isArray(exchangeData.availableExchanges)) {
        availableExchanges.value = exchangeData.availableExchanges
        updateAvailableExchangesFilters()
      }
      if (Array.isArray(exchangeData.nationalities)) {
        nationalities.value = exchangeData.nationalities
      }
      if (exchangeData.instructions) {
        instructions.value = exchangeData.instructions
      }
    } catch {
    }
  }
  applicationDataEl.value = document.querySelector('input[ref="applicationDataJson"]')
  if (applicationDataEl.value) {
    try {
      const storedApplicationData = JSON.parse(applicationDataEl.value.value)
      applicationData.value = {
        ...applicationData.value,
        ...storedApplicationData,
        subSpecialisationIds: Array.isArray(storedApplicationData.subSpecialisationIds)
          ? storedApplicationData.subSpecialisationIds
          : [],
        otherSubSpecialisation: typeof storedApplicationData.otherSubSpecialisation === 'string'
          ? storedApplicationData.otherSubSpecialisation
          : null,
        supportingDocument: storedApplicationData.supportingDocument && typeof storedApplicationData.supportingDocument === 'object'
          ? storedApplicationData.supportingDocument
          : {id: '', filename: '', url: ''},
      }
      normalizeStoredSubSpecialisationSelections()
    } catch {
    }
  }
  applicationDataInitialized.value = true
})
</script>
